import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SEOLanguage } from "@/data/seoTranslations";
import { getTranslatedDescription } from "@/data/structuredDataTranslations";
import { Button } from "@/components/ui/button";
import SpaContactModal from "@/components/SpaContactModal";
import { useServiceList } from "@/contexts/ServiceListContext";
import { GlassContainer } from "@/components/experimental/GlassUI";

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  actionButton?: {
    type: "serviceList" | "contact";
    label: string;
    onClick: () => void;
    trackingLabel?: string;
    trackingLocation?: string;
    trackingData?: Record<string, unknown>;
  };
}

const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  isOpen,
  onToggle,
  actionButton,
}) => {
  return (
    <div
      className="border-b border-swiss-cream/20 last:border-0"
      data-oid="pz1qbuw"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-6 text-left hover:bg-pebble-beige/20 transition-colors px-4 sm:px-6"
        aria-expanded={isOpen}
        data-oid="qffk3t7"
      >
        <h3
          className="text-lg sm:text-xl font-serif text-gray-900 pr-4"
          data-oid=":wdi4h5"
        >
          {question}
        </h3>
        <span className="flex-shrink-0 text-alpine-gold" data-oid="u_lkejk">
          {isOpen ? (
            <ChevronUp size={24} data-oid="11gj.hk" />
          ) : (
            <ChevronDown size={24} data-oid="99yev9_" />
          )}
        </span>
      </button>

      {isOpen && (
        <div className="px-4 sm:px-6 pb-6 animate-fadeIn" data-oid="cfhetfs">
          <p
            className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4"
            data-oid="h45wcv4"
          >
            {answer}
          </p>
          {actionButton && (
            <div className="mt-4" data-oid="faq-action-button">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  actionButton.onClick();
                }}
                trackingType={actionButton.type === "contact" ? "contact_modal" : "cta"}
                trackingLabel={actionButton.trackingLabel}
                trackingLocation={actionButton.trackingLocation}
                trackingData={actionButton.trackingData}
                variant="outline"
                className="px-4 py-2 text-sm border border-alpine-gold text-alpine-gold hover:bg-alpine-gold hover:text-white rounded-full font-medium transition-all duration-300"
                data-oid="faq-button"
              >
                {actionButton.label}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SpaFAQ: React.FC = () => {
  const { language } = useLanguage();
  const seoLang = language as SEOLanguage;
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [isSpaContactOpen, setIsSpaContactOpen] = useState(false);
  const { setIsTrayOpen } = useServiceList();

  const translations = {
    en: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about our spa services",
      viewServiceList: "View Service List",
      contactUs: "Contact Us",
    },
    de: {
      title: "Häufig gestellte Fragen",
      subtitle: "Alles, was Sie über unsere Spa-Dienstleistungen wissen müssen",
      viewServiceList: "Service-Liste anzeigen",
      contactUs: "Kontakt aufnehmen",
    },
    fr: {
      title: "Questions Fréquemment Posées",
      subtitle: "Tout ce que vous devez savoir sur nos services spa",
      viewServiceList: "Voir la liste des services",
      contactUs: "Nous contacter",
    },
    "zh-CN": {
      title: "常见问题",
      subtitle: "关于我们水疗服务的一切",
      viewServiceList: "查看服务列表",
      contactUs: "联系我们",
    },
    ko: {
      title: "자주 묻는 질문",
      subtitle: "스파 서비스에 대해 알아야 할 모든 것",
      viewServiceList: "서비스 목록 보기",
      contactUs: "문의하기",
    },
  };

  const content = translations[seoLang];

  const faqs = [
    {
      question: getTranslatedDescription("faqSpaBooking", seoLang),
      answer: getTranslatedDescription("faqSpaBookingAnswer", seoLang),
      actionButton: {
        type: "serviceList" as const,
        label: content.viewServiceList,
        onClick: () => setIsTrayOpen(true),
        trackingLabel: "View Service List",
        trackingLocation: "spa_faq_booking",
      },
    },
    {
      question: getTranslatedDescription("faqSpaMassages", seoLang),
      answer: getTranslatedDescription("faqSpaMassagesAnswer", seoLang),
      actionButton: {
        type: "serviceList" as const,
        label: content.viewServiceList,
        onClick: () => setIsTrayOpen(true),
        trackingLabel: "View Service List",
        trackingLocation: "spa_faq_massages",
      },
    },
    {
      question: getTranslatedDescription("faqSpaSaunaPackage", seoLang),
      answer: getTranslatedDescription("faqSpaSaunaPackageAnswer", seoLang),
      actionButton: {
        type: "serviceList" as const,
        label: content.viewServiceList,
        onClick: () => setIsTrayOpen(true),
        trackingLabel: "View Service List",
        trackingLocation: "spa_faq_sauna_package",
      },
    },
    {
      question: getTranslatedDescription("faqSpaHours", seoLang),
      answer: getTranslatedDescription("faqSpaHoursAnswer", seoLang),
    },
    {
      question: getTranslatedDescription("faqSpaPricing", seoLang),
      answer: getTranslatedDescription("faqSpaPricingAnswer", seoLang),
      actionButton: {
        type: "serviceList" as const,
        label: content.viewServiceList,
        onClick: () => setIsTrayOpen(true),
        trackingLabel: "View Service List",
        trackingLocation: "spa_faq_pricing",
      },
    },
    {
      question: getTranslatedDescription("faqSpaAccess", seoLang),
      answer: getTranslatedDescription("faqSpaAccessAnswer", seoLang),
      actionButton: {
        type: "contact" as const,
        label: content.contactUs,
        onClick: () => setIsSpaContactOpen(true),
        trackingLabel: "Contact Us",
        trackingLocation: "spa_faq_access",
        trackingData: { modalType: "spa", parentModal: "spa_faq" },
      },
    },
    {
      question: getTranslatedDescription("faqSpaCancellation", seoLang),
      answer: getTranslatedDescription("faqSpaCancellationAnswer", seoLang),
      actionButton: {
        type: "contact" as const,
        label: content.contactUs,
        onClick: () => setIsSpaContactOpen(true),
        trackingLabel: "Contact Us",
        trackingLocation: "spa_faq_cancellation",
        trackingData: { modalType: "spa", parentModal: "spa_faq" },
      },
    },
    {
      question: getTranslatedDescription("faqSpaPreparation", seoLang),
      answer: getTranslatedDescription("faqSpaPreparationAnswer", seoLang),
    },
  ];

  return (
    <section
      className="py-16 sm:py-20 lg:py-24"
      data-oid="fi:6xu."
    >
      <div
        className="container mx-auto px-4 sm:px-6 max-w-4xl"
        data-oid="z9f27or"
      >
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16" data-oid="thkenve">
          <GlassContainer intensity="low" className="inline-block px-10 py-6 backdrop-blur-md">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-serif text-gray-900 mb-4"
              data-oid="9yj4ri_"
            >
              {content.title}
            </h2>
            <p className="text-lg sm:text-xl text-gray-800" data-oid="l9.-f5k">
              {content.subtitle}
            </p>
          </GlassContainer>
        </div>

        {/* FAQ List */}
        <GlassContainer
          className="rounded-2xl overflow-hidden shadow-xl"
          intensity="medium"
          data-oid="u-t5dzd"
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              actionButton={faq.actionButton}
            />
          ))}
        </GlassContainer>

        {/* Contact CTA */}
        <div className="mt-12 text-center" data-oid=":tw7d16">
          <p
            className="text-base sm:text-lg text-gray-700 mb-6"
            data-oid="5j0qfe:"
          >
            {seoLang === "de"
              ? "Haben Sie weitere Fragen?"
              : seoLang === "fr"
                ? "Vous avez d'autres questions ?"
                : seoLang === "zh-CN"
                  ? "还有其他问题？"
                  : seoLang === "ko"
                    ? "더 궁금하신 점이 있으신가요?"
                    : "Have more questions?"}
          </p>
          <Button
            onClick={() => setIsSpaContactOpen(true)}
            trackingType="contact_modal"
            trackingLabel="Contact Spa Team"
            trackingLocation="spa_faq_section"
            trackingData={{ modalType: "spa" }}
            variant="outline"
            className="px-6 py-3 border-2 border-alpine-gold text-alpine-gold hover:bg-alpine-gold hover:text-white rounded-full font-medium transition-all duration-300"
            data-oid="o.fhocp"
          >
            {seoLang === "de"
              ? "Kontakt aufnehmen"
              : seoLang === "fr"
                ? "Nous contacter"
                : seoLang === "zh-CN"
                  ? "联系我们"
                  : seoLang === "ko"
                    ? "문의하기"
                    : "Contact Us"}
          </Button>
        </div>
      </div>

      {/* Spa Contact Modal */}
      <SpaContactModal
        isOpen={isSpaContactOpen}
        onClose={() => setIsSpaContactOpen(false)}
        parentModal="spa_faq"
        data-oid="ctn3w9n"
      />
    </section>
  );
};

export default SpaFAQ;
